import React from 'react';
import type { FormikProps } from 'formik';
import type { Contacts, Context, FieldVisibility } from '../../shared/types';
import { Avatar, Flex, TextV2, ButtonV2 } from '@withjoy/joykit';
import { Wrapper, Details, Footer } from '../components';
import { useConfirmationController } from './Confirmation.controller';
import { Phone, Email, Shipping } from '@withjoy/joykit/icons';
import { JoyLogoLoader } from '@shared/components/JoyLogoLoader';

interface ConfirmationProps
  extends Readonly<{
    formik: FormikProps<Contacts>;
    context?: Context;
    isMutationLoading: boolean;
    hasMutationError: boolean;
    emailRule?: FieldVisibility;
    phoneNumberRule?: FieldVisibility;
    mailingAddressRule?: FieldVisibility;
  }> {}

export const Confirmation: React.FC<ConfirmationProps> = ({
  formik,
  context,
  isMutationLoading,
  hasMutationError,
  emailRule = 'required',
  phoneNumberRule = 'optional',
  mailingAddressRule = 'optional',
}) => {
  const { tAdmin, handleOnModifyDetailsClick, handleOnLooksGoodClick } = useConfirmationController(formik);
  const headOfHousehold = formik.values.contacts[0];

  if (isMutationLoading) return <JoyLogoLoader loaderKey="contact-collector-address-confirm" />;

  return (
    <Wrapper>
      <Flex
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        paddingX="24px"
        width="100%"
      >
        <TextV2
          typographyVariant="hed6"
          marginTop={['24px', null, '4px']}
          textAlign="center"
        >
          {tAdmin.looksRight()}
        </TextV2>
        {context?.coupleNames && <Details coupleNames={context.coupleNames} />}
        <TextV2
          typographyVariant="hed4"
          marginTop="24px"
          marginBottom="24px"
          width="100%"
        >
          {tAdmin.contactInfo()}
        </TextV2>
        <Flex
          rowGap="40px"
          flexDirection="column"
          width="100%"
        >
          {formik.values.contacts.map((contact, index) => (
            <Flex
              key={index}
              rowGap="8px"
              flexDirection="column"
            >
              <Flex
                alignItems="center"
                justifyContent="flex-start"
                width="100%"
              >
                <Avatar
                  size="xs"
                  name={{ firstName: contact.firstName, lastName: contact.lastName }}
                />
                <TextV2
                  color="mono14"
                  typographyVariant="body2"
                  fontWeight="semibold"
                  marginLeft="8px"
                >{`${contact.firstName} ${contact.lastName}`}</TextV2>
              </Flex>
              {phoneNumberRule !== 'hidden' && contact.phoneNumber && (
                <Flex
                  alignItems="center"
                  justifyContent="flex-start"
                  width="100%"
                >
                  <Phone color="mono12" />
                  <TextV2
                    color="mono12"
                    typographyVariant="body2"
                    marginLeft="8px"
                  >
                    {contact.phoneNumber}
                  </TextV2>
                </Flex>
              )}
              {emailRule !== 'hidden' && contact.email && (
                <Flex
                  alignItems="center"
                  justifyContent="flex-start"
                  width="100%"
                >
                  <Email color="mono12" />
                  <TextV2
                    color="mono12"
                    typographyVariant="body2"
                    marginLeft="8px"
                  >
                    {contact.email}
                  </TextV2>
                </Flex>
              )}
            </Flex>
          ))}
        </Flex>
        {mailingAddressRule !== 'hidden' && headOfHousehold.address1 && (
          <>
            <TextV2
              typographyVariant="hed4"
              marginTop="40px"
              marginBottom="16px"
              width="100%"
            >
              {tAdmin.mailingAddress()}
            </TextV2>
            <Flex
              marginBottom="8px"
              width="100%"
            >
              <Shipping color="mono12" />
              <Flex
                flexDirection="column"
                alignItems="flex-start"
                marginLeft="8px"
              >
                <TextV2
                  color="mono12"
                  typographyVariant="body2"
                >{`${headOfHousehold.address1}${headOfHousehold.address2 ? ` ${headOfHousehold.address2}` : ''}`}</TextV2>
                <TextV2
                  color="mono12"
                  typographyVariant="body2"
                >{`${headOfHousehold.city},  ${headOfHousehold.region} ${headOfHousehold.postalCode}`}</TextV2>
                <TextV2
                  color="mono12"
                  typographyVariant="body2"
                >
                  {headOfHousehold.country}
                </TextV2>
              </Flex>
            </Flex>
          </>
        )}
        <ButtonV2
          intent="neutral"
          variant="outline"
          shape="rounded"
          marginTop="32px"
          onClick={handleOnModifyDetailsClick}
          fullWidth
        >
          {tAdmin.modifyDetails()}
        </ButtonV2>
        <ButtonV2
          intent="primary"
          shape="rounded"
          marginTop="16px"
          onClick={handleOnLooksGoodClick}
          fullWidth
        >
          {tAdmin.looksGood()}
        </ButtonV2>
        {hasMutationError && (
          <TextV2
            typographyVariant="body3"
            color="red"
            marginTop="16px"
          >
            {tAdmin.contactError()}
          </TextV2>
        )}
        <Footer />
      </Flex>
    </Wrapper>
  );
};
